
import MeaPage from "../../../components/MeaPage";
import { useParams } from "react-router-dom";
import campaignsAPI from "../../../services/campaigns";
import levelsAPI from "../../../services/levels";
import classesAPI from "../../../services/classes";
import { TabPanel, TabView } from "primereact/tabview";
import MailsListComponent from "./components/MailsListComponent";
import MeaTaskLogs from "../../../components/MeaTaskLogs";
import { ToFormatedDateForEvents } from "../../../utils/dates";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Chip } from "primereact/chip";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";

const RegistrationCampaignsDetailPage = () => {
  const params = useParams();
  const schoolId = params.schoolId;
  const registrationId = params.registrationId;
  const campaignId = params.campaignId;
  const accessToken = "";

  const campaignQuery = campaignsAPI.GetOne(schoolId, registrationId, accessToken, campaignId);
  const campaign = campaignQuery?.data?.data;
  const levelsQuery = levelsAPI.useGetList(schoolId, registrationId, accessToken, 1, 1000);
  const classesQuery = classesAPI.useGetList(schoolId, registrationId, accessToken, 1, 1000, {});
  const allLevels = levelsQuery?.data?.data || [];
  const allClasses = classesQuery?.data?.data || [];

  const levelNameMap = Object.fromEntries(allLevels.map((level) => [level.id, level.name]));
  const classNameMap = Object.fromEntries(allClasses.map((classItem) => [classItem.id, classItem.name]));

  const selectedLevelIds = (campaign?.level_ids || "")
    .split(",")
    .map((levelId) => levelId.trim())
    .filter(Boolean);

  const selectedClassIds = (campaign?.class_ids || "")
    .split(",")
    .map((classId) => classId.trim())
    .filter(Boolean);

  const targetedFamilyScope = [
    ...selectedClassIds.map((classId) => ({ label: `Classe: ${classNameMap[classId] || classId}` })),
    ...selectedLevelIds.map((levelId) => ({ label: `Niveau: ${levelNameMap[levelId] || levelId}` })),
  ];
  const hasTargetedFamilyScope = selectedLevelIds.length > 0 || selectedClassIds.length > 0;

  const mapRecipientLabel = (recipient) => {
    switch (recipient.trim()) {
      case "PREREGISTRED":
        return "Pré-inscrits";
      case "ENROLLED":
        return "Inscrits";
      case "UNREGISTRED":
        return "Désinscrits";
      case "WAITING_LIST":
        return "Liste d'attente";
      case "NEW":
        return "Nouveaux dossiers";
      case "PERSONALIZED":
        return "Personnalisé";
      case "TEACHERS":
        return "Enseignants";
      case "SECRETARIES":
        return "Secrétaires";
      case "ADMINISTRATORS":
        return "Équipe administrative";
      default:
        return recipient;
    }
  };

  const mapFilterLabel = (filter) => {
    switch (filter.trim()) {
      case "CONFIRMED_NOT_PAID":
      case "FILTER_ENROLLED_NOT_PAID":
        return "Confirmés avec contrat impayé";
      case "FILTER_TEACHERS_ONLY_THIS_TRAINING":
        return "Uniquement les enseignants de cette formation";
      default:
        return filter;
    }
  };

  const recipients = (campaign?.recipients_lists || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(mapRecipientLabel);

  const recipientFilters = (campaign?.recipients_filters || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map(mapFilterLabel);

  const statusConfig = {
    SENT: { label: "Envoyée", severity: "success" },
    FAILED: { label: "Erreur", severity: "danger" },
    PARTIAL: { label: "Partiellement envoyée", severity: "warning" },
    PENDING: { label: "En cours d'envoi", severity: "info" },
  };

  const status = statusConfig[campaign?.status] || statusConfig.PENDING;

  return (
    <>
      <MeaPage title="Campagne d'e-mails" showBackButton={true}>
        <div className="grid">
          <div className="col-12">
            <Card>
              <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-start gap-3">
                <div>
                  <div className="text-500 text-sm mb-1">Objet</div>
                  <h2 className="m-0 text-3xl">{campaign?.subject || "-"}</h2>
                </div>
                <div className="flex align-items-center gap-2 flex-wrap">
                  <Tag value={status.label} severity={status.severity} />
                  <Tag
                    value={campaign?.dry_run ? "Mode test" : "Envoi réel"}
                    severity={campaign?.dry_run ? "warning" : "info"}
                  />
                </div>
              </div>

              <Divider className="my-3" />

              <div className="grid">
                <div className="col-12 md:col-6">
                  <div className="flex justify-content-between py-2 border-bottom-1 border-200">
                    <span className="font-semibold">Date de création</span>
                    <span>{campaign?.created_at ? new Date(campaign?.created_at).toLocaleDateString("fr-FR") : "-"}</span>
                  </div>
                  <div className="flex justify-content-between py-2 border-bottom-1 border-200">
                    <span className="font-semibold">Date d'envoi</span>
                    <span>{campaign?.created_at ? ToFormatedDateForEvents(new Date(campaign?.created_at), false) : "-"}</span>
                  </div>
                  <div className="flex justify-content-between py-2">
                    <span className="font-semibold">Lien d'inscription inclus</span>
                    <span>{campaign?.include_personal_link ? "Oui" : "Non"}</span>
                  </div>
                </div>

                <div className="col-12 md:col-6">
                  <div className="surface-50 border-round p-3">
                    <div className="font-semibold mb-2">Destinataires</div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {recipients.length > 0 ? (
                        recipients.map((recipient, index) => (
                          <Chip key={recipient + index} label={recipient} />
                        ))
                      ) : (
                        <span className="text-500">Aucun destinataire</span>
                      )}
                    </div>

                    <div className="font-semibold mb-2">Filtres appliqués</div>
                    <div className="flex flex-wrap gap-2">
                      {recipientFilters.length > 0 ? (
                        recipientFilters.map((filter, index) => (
                          <Chip key={filter + index} label={filter} className="surface-200" />
                        ))
                      ) : (
                        <span className="text-500">Aucun filtre</span>
                      )}
                    </div>

                    <div className="font-semibold mt-3 mb-2">Niveaux / classes ciblés</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedClassIds.length > 0 ? (
                        selectedClassIds.map((classId, index) => (
                          <Chip key={classId + index} label={`Classe: ${classNameMap[classId] || classId}`} className="surface-200" />
                        ))
                      ) : selectedLevelIds.length > 0 ? (
                        selectedLevelIds.map((levelId, index) => (
                          <Chip key={levelId + index} label={`Niveau: ${levelNameMap[levelId] || levelId}`} className="surface-200" />
                        ))
                      ) : (
                        <Chip label="Tous les niveaux/classes" className="surface-200" />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {campaign?.dry_run && (
            <div className="col-12">
              <Message severity="warn" text="Cette campagne est en mode test: aucun e-mail réel n'a été envoyé." />
            </div>
          )}

          <div className="col-12">
            <Card title="Contenu de l'e-mail">
              {campaign?.body ? (
                <div className="border-1 border-200 border-round p-3" dangerouslySetInnerHTML={{ __html: campaign?.body }} />
              ) : (
                <Message severity="info" text="Aucun contenu disponible." />
              )}
            </Card>
          </div>

          <div className="col-12">
            <Card className="p-0">
              <TabView>
                <TabPanel header="Emails">
                  <MailsListComponent schoolId={schoolId} trainingId={registrationId} campaignId={campaignId} />
                </TabPanel>
                <TabPanel header="Logs">
                  <MeaTaskLogs schoolId={schoolId} trainingId={registrationId} jobId={campaign?.job_id} accessToken={accessToken} />
                </TabPanel>
              </TabView>
            </Card>
          </div>
        </div>
      </MeaPage>
    </>
  );
};

export default RegistrationCampaignsDetailPage;
